import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, FileText, Upload, Settings, LogOut, History, Menu, X, User, ChevronLeft, Search, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useUser } from '../../contexts/UserContext';
interface NavigationProps {
  onLogout?: () => void;
}
// Public navigation items (no auth required)
const publicNavItems = [{
  name: 'Discover',
  path: '/app',
  icon: Home,
  exact: true
}, {
  name: 'Browse',
  path: '/app/transcriptions',
  icon: FileText,
  exact: false
}, {
  name: 'Search',
  path: '/app/search',
  icon: Search,
  exact: false
}];
// Authenticated navigation items (require phone-based auth)
const authenticatedNavItems = [{
  name: 'My Dashboard',
  path: '/app/me',
  icon: Home,
  exact: true
}, {
  name: 'My History',
  path: '/app/me/history',
  icon: History,
  exact: true
}, {
  name: 'Batch Process',
  path: '/app/me/batch',
  icon: Upload,
  exact: true
}, {
  name: 'Settings',
  path: '/app/me/settings',
  icon: Settings,
  exact: true
}];
export function Navigation({
  onLogout
}: NavigationProps) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  const {
    user,
    session
  } = useUser();
  const navItems = session ? authenticatedNavItems : publicNavItems;
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', isCollapsed.toString());
  }, [isCollapsed]);
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);
  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };
  return <>
      {/* Desktop Navigation */}
      <nav className={cn('h-[calc(100vh-2rem)] sticky top-4 mx-4 p-5 hidden md:flex md:flex-col bg-card/95 rounded-2xl shadow-lg ios-blur overflow-hidden transition-all duration-300 hover:shadow-xl', isCollapsed ? 'w-20' : 'w-64')} data-id="element-670">
        {/* Toggle Button */}
        <button onClick={toggleCollapsed} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-secondary text-foreground/50 hover:text-foreground transition-colors z-10" aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} data-id="element-671">
          {isCollapsed ? <ChevronsRight size={18} data-id="element-672" /> : <ChevronsLeft size={18} data-id="element-673" />}
        </button>

        {/* Logo */}
        <div className="flex-shrink-0 mb-6 px-2" data-id="element-674">
          {!isCollapsed ? <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6tsscMEhsZ3L4JwT2r374e/ScribeTok-logo.png" alt="ScribeTok Logo" className="h-10 w-auto transition-opacity duration-200" data-id="element-675" /> : <div className="flex items-center justify-center" data-id="element-676">
              <FileText className="h-8 w-8 text-primary" data-id="element-677" />
            </div>}
        </div>

        {/* User Info */}
        {user && <div className={cn('flex-shrink-0 mb-4 p-3 bg-primary/5 rounded-xl transition-all duration-200', isCollapsed ? 'flex flex-col items-center' : 'flex items-center')} data-id="element-678">
            <div className={cn('rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0', isCollapsed ? 'w-10 h-10' : 'w-8 h-8 mr-3')} data-id="element-679">
              <User size={isCollapsed ? 18 : 14} className="text-primary" data-id="element-680" />
            </div>
            {!isCollapsed && <div className="overflow-hidden flex-1" data-id="element-681">
                <p className="text-sm font-medium truncate" data-id="element-682">{user.email}</p>
                <div className="flex items-center flex-wrap gap-1" data-id="element-683">
                  <p className="text-xs text-muted-foreground" data-id="element-684">
                    {user.plan.isPro ? 'Pro Plan' : 'Free Plan'}
                  </p>
                  {!user.plan.isPro && <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded-full whitespace-nowrap" data-id="element-685">
                      {Math.max(0, 3 - user.usage.minutesUsed)} credits left
                    </span>}
                </div>
              </div>}
          </div>}

        {/* Navigation Items */}
        <div className="flex-grow overflow-y-auto min-h-0" data-id="element-686">
          <div className="space-y-1" data-id="element-687">
            {navItems.map(item => {
            const isActive = item.exact ? location.pathname === item.path : location.pathname === item.path || item.path !== '/app' && location.pathname.startsWith(item.path);
            const Icon = item.icon;
            return <Link key={item.path} to={item.path} className={cn('flex items-center h-11 px-3 py-2 rounded-xl text-sm font-medium', 'transition-colors duration-200 group relative', isActive ? 'text-primary-foreground tiktok-gradient' : 'text-foreground/70 hover:text-foreground hover:bg-secondary', isCollapsed && 'justify-center')} title={isCollapsed ? item.name : undefined} data-id="element-688">
                  <Icon size={20} className={cn(isCollapsed ? '' : 'mr-3 flex-shrink-0', isActive ? 'text-primary-foreground' : 'text-foreground/50 group-hover:text-foreground/70')} data-id="element-689" />
                  {!isCollapsed && <span className="truncate" data-id="element-690">{item.name}</span>}
                </Link>;
          })}
          </div>
        </div>

        {/* Logout Button */}
        {onLogout && session && <div className="flex-shrink-0 mt-auto pt-4 border-t border-border/40" data-id="element-691">
            <button onClick={onLogout} className={cn('flex w-full items-center h-11 px-3 py-2 rounded-xl text-sm font-medium text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors duration-200', isCollapsed && 'justify-center')} title={isCollapsed ? 'Sign Out' : undefined} data-id="element-692">
              <LogOut size={20} className={cn(isCollapsed ? '' : 'mr-3 flex-shrink-0', 'text-foreground/50')} data-id="element-693" />
              {!isCollapsed && <span className="truncate" data-id="element-694">Sign Out</span>}
            </button>
          </div>}
      </nav>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border z-50" data-id="element-695">
        <div className="flex justify-around items-center h-16 px-4" data-id="element-696">
          {navItems.map(item => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return <Link key={item.path} to={item.path} className={cn('flex flex-col items-center justify-center flex-1 h-full transition-colors', isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground')} data-id="element-697">
                <Icon size={22} data-id="element-698" />
                <span className="text-xs mt-1" data-id="element-699">{item.name}</span>
              </Link>;
        })}
        </div>
      </nav>
    </>;
}